import React from 'react';
import I18n from 'i18n-js';

import PriceDisplayRange from '@reverbdotcom/commons/src/components/price_display_range';
import { PriceDisplay } from '@reverbdotcom/commons';
import { formatAmountCents } from '@reverbdotcom/commons/src/money';
import { I18N } from '@reverbdotcom/commons/src/components/translate';

interface IMoney {
  amountCents?: number;
  currency?: string;
}

interface IProps {
  priceLow: IMoney;
  priceHigh: IMoney;
  priceMiddle: IMoney;
  priceMiddleThirtyDaysAgo: IMoney;
  displayRegionalEstimateCaveat: boolean;
}

export function PriceChangeDisplay({ priceDifferenceCents, currency }) {
  const hasPriceIncreased = priceDifferenceCents > 0;

  const formattedDifference = formatAmountCents({
    amountCents: Math.abs(priceDifferenceCents),
    currency,
  }, {
    precision: 0,
  });

  const arrowClass = hasPriceIncreased ? 'icon-r-arrow-up color-green' : 'icon-r-arrow-down color-red';

  return (
    <div className="price-change-display">
      <div className="d-flex fx-align-center">
        <span>
          {formattedDifference}
        </span>
        <i className={arrowClass} />
      </div>
      <div className="price-change-display__price-description">
        {I18n.t('discovery.priceGuideTool.show.form.results.pastThirtyDays')}
      </div>
    </div>
  );
}

interface IAveragePriceContainerProps {
  priceMiddle: IMoney;
  priceMiddleThirtyDaysAgo: IMoney;
  hideAveragePriceItemSold?: boolean;
}

export function AveragePriceContainer({
  priceMiddle,
  priceMiddleThirtyDaysAgo,
  hideAveragePriceItemSold = false,
}:  IAveragePriceContainerProps) {
  if (!priceMiddle.amountCents || !priceMiddleThirtyDaysAgo.amountCents) return null;

  const priceMiddleDifferenceCents = priceMiddle.amountCents - priceMiddleThirtyDaysAgo.amountCents;
  const showAveragePrice = !hideAveragePriceItemSold;

  return (
    <div className="average-price-container">
      {
        priceMiddleDifferenceCents !== 0 && (
          <div className="size-120 weight-bold mr-5">
            <PriceChangeDisplay
              priceDifferenceCents={priceMiddleDifferenceCents}
              currency={priceMiddle.currency}
            />
          </div>
        )
      }

      {showAveragePrice && (
        <div className="size-120 weight-bold">
          <PriceDisplay
            amountCents={priceMiddle.amountCents}
            currency={priceMiddle.currency}
            display={formatAmountCents(priceMiddle, {
              precision: 0,
            })}
          />

          <div className="price-change-display__price-description">
            {I18n.t('discovery.priceGuideTool.show.form.results.averagePriceItemSold')}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PriceGuideToolEstimates({
  priceLow,
  priceHigh,
  priceMiddle,
  priceMiddleThirtyDaysAgo,
  displayRegionalEstimateCaveat = false,
}: IProps) {
  return (
    <div className="price-guide-tool-estimates">
      <h3 className="size-100 weight-bold mb-2">
        {I18n.t('discovery.priceGuideTool.show.form.results.estimatedPriceRange')}
      </h3>
      <div className="size-200 weight-bold">
        <PriceDisplayRange
          moneyLow={priceLow}
          moneyHigh={priceHigh}
          displayConfig={{ precision: 0 }}
        />
      </div>

      <AveragePriceContainer
        priceMiddle={priceMiddle}
        priceMiddleThirtyDaysAgo={priceMiddleThirtyDaysAgo}
      />

      {displayRegionalEstimateCaveat && (
        <div className="mt-1 color-secondary style-italic">
          <I18N
            html
            text="discovery.priceGuideTool.show.form.results.regionalEstimateCaveat"
          />
        </div>
      )}
    </div>
  );
}
